import { VendorCapabilityValues, type VendorCapability } from '../generated/enums';

/**
 * What a vendor does for the shop, in the words the shop uses (spec Sections 10, 11 and 16).
 *
 * <p>
 * <strong>Its own module because the enum it maps is generated.</strong> `VendorCapability` is
 * projected from the C# enum, so a fourth capability added on the server — Section 23's logistics
 * partner is the one already anticipated — would otherwise arrive with no label and the screen
 * would render an empty chip against a vendor that does something. Two guards, in that order: the
 * `Record<Exclude<…>>` below makes it a **compile error**, which is what this codebase wants of a
 * catalogue that lives in code; and `everyCapabilityHasALabel` covers what a type cannot, since a
 * label could be added as an empty string and satisfy the type while rendering nothing.
 * </p>
 *
 * <p>
 * A wording map rather than `humanizeEnum`, because the enum names are the wrong words for a
 * screen: `Laundry` reads as a place and `Tailor` as a person, while the column is a statement
 * about what this vendor <em>does</em>.
 * </p>
 */
export const capabilityLabels: Record<Exclude<VendorCapability, 'None'>, string> = {
  Laundry: 'Cleaning',
  Tailor: 'Alterations',
  Supplier: 'Supply',
};

/**
 * One line of explanation each, for the drawer's tick boxes.
 *
 * The label alone does not tell a shop owner that ticking "Cleaning" is what puts this vendor in
 * the dispatch list a cleaning job will pick from — which is the whole reason the tick boxes exist
 * rather than three separate screens.
 */
export const capabilityHints: Record<Exclude<VendorCapability, 'None'>, string> = {
  Laundry: 'Washes and reconditions garments',
  Tailor: 'Alters and repairs garments',
  Supplier: 'Sells you stock',
};

/**
 * The capabilities a vendor can actually be given, in the order a screen should read them.
 *
 * Derived from the generated list rather than written out a second time, so a capability added on
 * the server appears in the drawer without anyone remembering to add it — and `None`, which means
 * the absence of one and which no stored vendor may claim, is dropped here in the one place.
 */
export const selectableCapabilities: Exclude<VendorCapability, 'None'>[] =
  VendorCapabilityValues.filter(
    (value): value is Exclude<VendorCapability, 'None'> => value !== 'None',
  );

/**
 * The chips above the list, and the query Section 10's dispatch screen will make. `undefined` is
 * "all", which is not a capability and deliberately not a value the API is sent.
 */
export const capabilityFilters: { value: VendorCapability | undefined; label: string }[] = [
  { value: undefined, label: 'All' },
  ...selectableCapabilities.map((value) => ({ value, label: capabilityLabels[value] })),
];

/**
 * The shop's word for one capability.
 *
 * `None` falls through to itself rather than to a friendlier string: it should never reach a
 * screen, and printing something reassuring for a vendor that does nothing would hide the defect
 * rather than show it.
 */
export function capabilityLabel(capability: VendorCapability): string {
  return capability === 'None' ? capability : capabilityLabels[capability];
}

/**
 * Whether every capability the server can send has a word for it here. Exported so the test can ask
 * the question rather than restating the list.
 */
export function everyCapabilityHasALabel(): boolean {
  return selectableCapabilities.every(
    (capability) => Boolean(capabilityLabels[capability]) && Boolean(capabilityHints[capability]),
  );
}
